/**
 * Browser and device flags for display capture.
 * iPhone/iPad Chrome is still WebKit, so isChrome can be true there while isChromium stays false.
 *
 * @typedef {Object} CaptureEnvironment
 * @property {boolean} isChrome Google Chrome, including Chrome on iOS
 * @property {boolean} isChromium Desktop Chromium: Chrome, Edge, Opera, Brave, Vivaldi, Arc, and others. False on phones.
 * @property {boolean} isFirefox Firefox and Firefox-based browsers (including iOS Firefox)
 * @property {boolean} isSafari Safari, including Safari on iPhone/iPad
 * @property {boolean} isMobile Phone or tablet
 * @property {boolean} isApple Mac, iPhone, iPad, or iPod
 */

const EMPTY_ENVIRONMENT = {
  isChrome: false,
  isChromium: false,
  isFirefox: false,
  isSafari: false,
  isMobile: false,
  isApple: false,
}

function brandNames(uaData) {
  const list = uaData?.brands || []
  return list.map((entry) => (typeof entry === "string" ? entry : entry?.brand || ""))
}

/**
 * @param {Navigator} [nav]
 * @returns {CaptureEnvironment}
 */
export function detectCaptureEnvironment(nav = typeof navigator !== "undefined" ? navigator : undefined) {
  if (!nav) return { ...EMPTY_ENVIRONMENT }

  const ua = nav.userAgent || ""
  const uaData = nav.userAgentData
  const platform = uaData?.platform || nav.platform || ""

  const isIpadOs = platform === "MacIntel" && (nav.maxTouchPoints || 0) > 1
  const isIos = /iPhone|iPad|iPod/i.test(ua) || isIpadOs
  const isAndroid = /Android/i.test(ua)
  const isMobile = Boolean(uaData?.mobile) || isIos || isAndroid || /Mobile/i.test(ua)
  const isApple =
    isIos ||
    uaData?.platform === "macOS" ||
    /Mac/i.test(platform) ||
    /Mac OS X|Macintosh/i.test(ua)

  const brands = brandNames(uaData)
  const hasBrand = (pattern) => brands.some((brand) => pattern.test(brand))

  const isEdge = /Edg\/|EdgA\/|EdgiOS/.test(ua) || hasBrand(/Microsoft Edge/i)
  const isOpera = /OPR\/|Opera/.test(ua) || hasBrand(/^Opera/i)
  const isIosChrome = /CriOS/.test(ua)
  const isIosFirefox = /FxiOS/.test(ua)
  const isFirefox =
    /Firefox\/|FxiOS\/|Focus\/|Waterfox|LibreWolf|TorBrowser/i.test(ua) ||
    hasBrand(/Firefox/i)
  const isChromiumFamily =
    /Chrome\/|Chromium\/|CriOS|Edg\/|EdgA\/|EdgiOS|OPR\/|Brave|Vivaldi|SamsungBrowser/i.test(ua) ||
    hasBrand(/Chromium|Google Chrome|Microsoft Edge|Opera|Brave|Vivaldi/i)

  const isChrome =
    ((/Chrome\//.test(ua) && !/Chromium\//.test(ua)) || isIosChrome || hasBrand(/Google Chrome/i)) &&
    !isEdge &&
    !isOpera &&
    !isFirefox
  const isChromium = isChromiumFamily && !isMobile && !isIosChrome && !isFirefox
  const isSafari =
    !isFirefox &&
    ((/Safari\//.test(ua) && !isChromiumFamily && !isIosFirefox) ||
      (isIos && !isIosChrome && !isIosFirefox && !/EdgiOS/.test(ua)))

  return { isChrome, isChromium, isFirefox, isSafari, isMobile, isApple }
}

/**
 * Share-dialog instructions. Screen and window stay available on every desktop browser.
 * @param {CaptureEnvironment} [env]
 * @returns {"mobile" | "safari" | "chromium" | "firefox" | "standard"}
 */
export function getShareAudioGuidance(env = detectCaptureEnvironment()) {
  if (env.isMobile) return "mobile"
  if (env.isFirefox) return "firefox"
  if (env.isChromium || env.isChrome) return "chromium"
  if (env.isSafari) return "safari"
  return "standard"
}

const STANDARD_DISPLAY_MEDIA_OPTIONS = {
  video: true,
  audio: true,
}

const CHROMIUM_DISPLAY_MEDIA_OPTIONS = {
  video: true,
  audio: {
    suppressLocalAudioPlayback: false,
  },
  systemAudio: "include",
  windowAudio: "system",
  monitorTypeSurfaces: "include",
  surfaceSwitching: "include",
  selfBrowserSurface: "exclude",
}

/**
 * Options for getDisplayMedia.
 * A bare displaySurface value is an exact constraint and hides the other surfaces,
 * so Screen and Window are requested with video: true plus the audio flags that browser accepts.
 *
 * @param {CaptureEnvironment} [env]
 */
export function buildDisplayMediaOptions(env = detectCaptureEnvironment()) {
  const desktopChromium = !env.isMobile && !env.isFirefox && (env.isChromium || env.isChrome)
  if (desktopChromium) return { ...CHROMIUM_DISPLAY_MEDIA_OPTIONS }

  // Firefox, Safari, phones, and every other current browser.
  // video: true keeps Screen and Window in the picker. audio: true asks for their audio.
  return { ...STANDARD_DISPLAY_MEDIA_OPTIONS }
}

export const DISPLAY_MEDIA_OPTIONS_FALLBACK = {
  video: true,
  audio: true,
}

/**
 * Microphone constraints that Chrome, Firefox, Safari, and mobile browsers can open.
 * Bare numbers are exact constraints; Safari and Firefox reject sampleRate/latency they cannot guarantee.
 *
 * @param {string} [deviceId]
 * @param {CaptureEnvironment} [env]
 */
export function buildMicrophoneConstraints(deviceId, env = detectCaptureEnvironment()) {
  const device = deviceId ? { deviceId: { ideal: deviceId } } : {}
  const processing = {
    echoCancellation: { ideal: true },
    noiseSuppression: { ideal: true },
    autoGainControl: { ideal: true },
    ...device,
  }

  if (env.isSafari || env.isFirefox || env.isMobile || !env.isChromium) {
    return processing
  }

  return {
    channelCount: { ideal: 1 },
    sampleRate: { ideal: 48000 },
    latency: { ideal: 0 },
    ...processing,
  }
}

/**
 * Opens the selected microphone. Retries with a plain audio request if the browser rejects a constraint.
 * @param {string} [deviceId]
 * @param {CaptureEnvironment} [env]
 * @returns {Promise<MediaStream>}
 */
export async function acquireMicrophoneStream(deviceId, env = detectCaptureEnvironment()) {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    throw new Error("This browser does not support microphone capture.")
  }

  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: buildMicrophoneConstraints(deviceId, env),
    })
  } catch (err) {
    const retry =
      err?.name === "OverconstrainedError" ||
      err?.name === "ConstraintNotSatisfiedError" ||
      err?.name === "TypeError" ||
      err?.name === "NotReadableError"
    if (!retry) throw err

    const audio = deviceId ? { deviceId: { ideal: deviceId } } : true
    return await navigator.mediaDevices.getUserMedia({ audio })
  }
}
